import type { ApplicationContext } from '@hc-frontend/core-context';
import { JourneyAlias, ProductType } from '@hc-frontend/core-types';
import type { IOperationError } from '@hc-frontend/core-utils-business-operation';
import {
  operationConcat,
  unsafe_validateEntity,
} from '@hc-frontend/core-utils-business-operation';

import type { PartialSurveyAnswers } from '../../entities';
import { MoveToNextPlateOptions, PlateName } from '../../entities';
import type { Plate } from '../../entities/journey';
import { Journey } from '../../entities/journey';
import type { JourneyRepository, MoveToPlateResult } from '../../repositories';
import type { GetAnswersUseCase } from '../get-answers/get-answers';
import type { UpdateLeadProductUseCase } from '../update-lead-product';

export type MoveToNextPlateUseCase = (
  options: MoveToNextPlateOptions,
) => Promise<MoveToPlateResult>;

export type MoveToNextPlateFactoryParams = Pick<ApplicationContext, 'log'> &
  Pick<JourneyRepository, 'getJourney' | 'moveToPlate' | 'getNextPlate'> & {
    updateLeadProduct: (
      currentProductType: ProductType,
      newProductType: ProductType,
    ) => ReturnType<UpdateLeadProductUseCase>;
    getAnswers: GetAnswersUseCase;
  };

async function logOperation<T extends string>(
  log: ApplicationContext['log'],
  result: IOperationError<T>,
) {
  log({
    context: 'server',
    message: result.code,
    type: 'error',
    data: {
      rootCause: result.rootCause,
    },
  });
}

function handleMoveToNextPlateFactory({
  getJourney,
  log,
  getNextPlate,
  getAnswers,
}: Omit<MoveToNextPlateFactoryParams, 'updateLeadProduct'>) {
  return async ({
    journeyAlias,
    userId,
    sessionId,
    domainUserId,
    domainSessionId,
    currentPlate,
    surveyAnswers,
  }: Omit<MoveToNextPlateOptions, 'productType'>) => {
    const execute = operationConcat(getJourney)
      .then(unsafe_validateEntity(Journey, 'failedToGetJourney'))
      .append(
        operationConcat(getAnswers).onErrorDefaultTo<PartialSurveyAnswers>(() =>
          Promise.resolve({}),
        ),
      )
      .then(async ([parsedJourney, answers]) =>
        getNextPlate({
          currentPlate,
          journey: parsedJourney,
          answers: {
            ...answers,
            ...surveyAnswers,
          },
        }),
      )
      .onErrorTap((error) => logOperation(log, error))
      .done();

    return execute(
      [journeyAlias],
      [sessionId, userId, domainSessionId, domainUserId],
    );
  };
}

export function moveToNextPlateFactory({
  getJourney,
  moveToPlate,
  updateLeadProduct,
  getNextPlate,
  getAnswers,
  log,
}: MoveToNextPlateFactoryParams): MoveToNextPlateUseCase {
  const handleMoveToNextPlate = handleMoveToNextPlateFactory({
    getJourney,
    moveToPlate,
    getNextPlate,
    getAnswers,
    log,
  });

  const handleMedicareRedirect = (
    journeyAlias: JourneyAlias,
    options: MoveToNextPlateOptions,
  ) =>
    operationConcat(handleMoveToNextPlate)
      .then((result) =>
        moveToPlate({
          plate: result,
          journeyAlias,
        }),
      )
      .done()({
      ...options,
      journeyAlias,
      surveyAnswers: undefined,
      currentPlate: undefined,
    });

  const handleHealthRedirect = (productType: ProductType, plateToMove: Plate) =>
    operationConcat(updateLeadProduct)
      .then(() =>
        moveToPlate({
          plate: plateToMove,
          journeyAlias: JourneyAlias.Enum.HEALTH_DEFAULT,
          targetsExternalApp: true,
        }),
      )
      .done()(productType, ProductType.Enum.HEALTH);

  return operationConcat(
    unsafe_validateEntity(MoveToNextPlateOptions, 'failedToMoveToPlate'),
  )
    .thenJoin(handleMoveToNextPlate)
    .then(([parsedOptions, plateToMove]) => {
      if (plateToMove.name === PlateName.Enum.REDIRECT_UNDER_65) {
        return handleHealthRedirect(parsedOptions.productType, plateToMove);
      }
      if (plateToMove.name === PlateName.Enum.REDIRECT_MEDICARE_ADVANTAGE) {
        return handleMedicareRedirect(
          JourneyAlias.Enum.MEDICARE_ADVANTAGE,
          parsedOptions,
        );
      }
      if (plateToMove.name === PlateName.Enum.REDIRECT_MEDICARE_SUPPLEMENT) {
        return handleMedicareRedirect(
          JourneyAlias.Enum.MEDICARE_SUPPLEMENT,
          parsedOptions,
        );
      }

      return moveToPlate({
        plate: plateToMove,
        journeyAlias: parsedOptions.journeyAlias,
      });
    })
    .onErrorTap(async (result) => logOperation(log, result))
    .done();
}
